import { createReducer, AnyAction } from '@reduxjs/toolkit';
import { setTimeLineEntryPropTimeZone } from './reducer-utils';
import InitialState from '../reducers/initial-state';
import { toggleDrawer, toggleDrawerContent, fetchContexts, logout } from '../actions/ui';
import {
  codeBenchActions,
  cmxDateTime,
  commonEnums,
  launchPageActions,
  AmplifyCore
} from '@codametrix/ui-common';
import { CheckboxOption } from '../components/checkbox-menu/checkbox-menu';
import { jumpContext } from '../actions/contexts.action';
import { resolveTimelineTimeZone } from '../utils/timezone';

const { conditionalUtils } = AmplifyCore;

export const reducer = createReducer(InitialState.patientTimeline, builder => {
  builder.addCase(logout.type, () => ({ ...InitialState.patientTimeline }));
  builder.addCase(jumpContext.pending.type, () => ({ ...InitialState.patientTimeline }));

  builder.addCase(toggleDrawerContent.type, (state, action: AnyAction) => {
    return { ...state, isOpen: action.payload };
  });

  builder.addCase(toggleDrawer.type, (state, action: AnyAction) => {
    return { ...state, isDrawerOpen: action.payload };
  });

  // fetchContexts is now RTK thunk
  builder.addCase(fetchContexts.pending, state => {
    return {
      ...state,
      isDrawerOpen: false,
      entries: [],
      entriesLoading: false,
      entriesLastPage: false,
      entryPage: 1,
      entriesMessageCounts: [],
      entriesMessageCountsLoading: false,
      entriesSumCounts: 0
    };
  });

  builder.addCase('LOAD_FROM_URL/pending', state => {
    return {
      ...state,
      dateFilter: commonEnums.DateFilterLabelEnum.ALL_EVENTS,
      dateFilterStr: '',
      filterTypes: [],
      entries: []
    };
  });

  // navigatePatient is RTK thunk; pending carries arg in meta.arg
  builder.addCase(codeBenchActions.navigatePatient.pending, (state, action) => {
    return {
      ...state,
      activeCaseRun: action.meta.arg,
      isDrawerOpen: true,
      entriesFailed: false,
      entries: [],
      entriesLoading: false,
      entriesLastPage: false,
      entryPage: 1,
      entriesMessageCounts: [],
      entriesMessageCountsLoading: false,
      entriesSumCounts: 0,
      isFirstLoad: true
    };
  });

  builder.addCase(codeBenchActions.navigatePatient.fulfilled, state => {
    return { ...state, isOpen: true };
  });

  builder.addCase(codeBenchActions.getPatientMessageCounts.pending, state => {
    return {
      ...state,
      entriesMessageCounts: [],
      entriesMessageCountsLoading: true,
      entriesSumCounts: 0
    };
  });

  builder.addCase(codeBenchActions.getPatientMessageCounts.rejected, state => {
    return {
      ...state,
      entriesMessageCounts: [],
      entriesMessageCountsLoading: false,
      entriesSumCounts: 0
    };
  });

  const handleMessageCountsDone = (state: any, action: any) => {
    const entriesMessageCounts = action.payload;
    return {
      ...state,
      entriesMessageCounts,
      entriesMessageCountsLoading: false,
      entriesSumCounts:
        entriesMessageCounts?.length > 0
          ? entriesMessageCounts
              .map((entry: any) => entry.count)
              .reduce(
                (prevEntryCount: number, currentEntryCount: number) =>
                  prevEntryCount + currentEntryCount
              )
          : 0
    };
  };

  builder.addCase(codeBenchActions.getPatientMessageCounts.fulfilled, handleMessageCountsDone);
  builder.addCase(codeBenchActions.getPatientMessageCountsWithFilter.fulfilled, handleMessageCountsDone);

  builder.addCase(codeBenchActions.getCaseEntries.pending, state => {
    return {
      ...state,
      entriesLoading: true,
      isFirstLoad: false
    };
  });

  builder.addCase(codeBenchActions.getCaseEntriesWithFilter.pending, state => {
    return {
      ...state,
      entriesLoading: true,
      entries: [],
      isFirstLoad: false
    };
  });

  builder.addCase(codeBenchActions.caseOrganization, (state, action) => {
    //@ts-ignore
    return { ...state, organization: action.payload };
  });

  const handleCaseEntriesDone = (state: any, action: any) => {
    const timeline = action.payload;
    const params = action.meta.arg;

    if (timeline.success === false) {
      return {
        ...state,
        entriesLoading: false,
        entriesFailed: true
      };
    }

    const timeZonestr = resolveTimelineTimeZone({
      caseRunTimeZone: params?.caseRun?.tenant_timezone,
      activeCaseRunTimeZone: state?.activeCaseRun?.tenant_timezone,
      organizationTimeZone:
        params?.organizationTimeZone ?? state?.organization?.timeZoneFullName
    });

    const updatedTimeline = timeline?.content?.map((entry: any) => {
      const updatedEntry = { ...entry };
      ['meta.event_datetime', 'note_date', 'service_date'].forEach(
        dateProperty => {
          setTimeLineEntryPropTimeZone(
            updatedEntry,
            dateProperty,
            timeZonestr
          );
        }
      );
      return updatedEntry;
    });

    const sortedEntries = [
      ...state.entries,
      ...updatedTimeline
    ]?.sort((groupA: CMxAPI.TimelineEntry, groupB: CMxAPI.TimelineEntry) =>
      cmxDateTime.sortByDate(
        groupA?.message?.type === 'MDM'
          ? groupA?.note_date
          : groupA?.message?.type === 'SIU'
          ? groupA?.service_date
          : groupA?.meta?.event_datetime,
        groupB?.message?.type === 'MDM'
          ? groupB?.note_date
          : groupB?.message?.type === 'SIU'
          ? groupB?.service_date
          : groupB?.meta?.event_datetime
      )
    );

    const isTimelineInfiniteLoadOn = conditionalUtils.capabilityValue(
      params?.timelineInfiniteLoad
    );

    return {
      ...state,
      entries: sortedEntries,
      entryPage: timeline?.pageable?.pageNumber,
      numberOfElements: timeline?.numberOfElements,
      entriesLoading: false,
      entriesLastPage: timeline?.last,
      filterTypes: isTimelineInfiniteLoadOn ? state.filterTypes : [],
      typesCounts: fillTypesCounts(sortedEntries),
      dateFilter: isTimelineInfiniteLoadOn
        ? state.dateFilter
        : commonEnums.DateFilterLabelEnum.ALL_EVENTS
    };
  };

  builder.addCase(codeBenchActions.getCaseEntries.fulfilled, handleCaseEntriesDone);
  builder.addCase(codeBenchActions.getCaseEntriesWithFilter.fulfilled, handleCaseEntriesDone);

  builder.addCase(codeBenchActions.getCaseEntries.rejected, state => {
    return {
      ...state,
      entries: [],
      entryPage: 1,
      numberOfElements: 0,
      entriesLoading: false,
      entriesLastPage: false,
      filterTypes: []
    };
  });

  builder.addCase(codeBenchActions.setFilterTypes, (state, action) => {
    return {
      ...state,
      filterTypes: action.payload as CheckboxOption[]
    };
  });

  builder.addCase(codeBenchActions.setFilterDates, (state, action) => {
    return {
      ...state,
      dateFilter: action.payload?.dateFilter
    };
  });

  builder.addCase(codeBenchActions.setFilterTypesInfinite, (state, action) => {
    return {
      ...state,
      filterTypes: action.payload as CheckboxOption[],
      entries: []
    };
  });

  builder.addCase(codeBenchActions.fetchOrderTypeStatus.fulfilled, (state, action) => {
    return {
      ...state,
      orderTypeStatus: action.payload?.rowValues ?? []
    };
  });

  builder.addCase(codeBenchActions.setFilterDatesInfinite, (state, action) => {
    return {
      ...state,
      dateFilter: action.payload?.dateFilter,
      dateFilterStr: action.payload?.dateFilterStr ?? '',
      entries: []
    };
  });
});

const fillTypesCounts = (entries: CMxAPI.TimelineEntry[]): CMx.TypesCounts => {
  const counts: { [key in CMxAPI.MessageType]: number } = {
    ADT: 0,
    SIU: 0,
    ORM: 0,
    RDS: 0,
    MDM: 0,
    DFT: 0
  };

  for (const key in commonEnums.EntriesMessageType) {
    if (
      Object.prototype.hasOwnProperty.call(commonEnums.EntriesMessageType, key)
    ) {
      const messageType = key as CMxAPI.MessageType;
      counts[messageType] = entries.filter(
        entry => entry?.message?.type === messageType
      ).length;
    }
  }

  return counts;
};

export default reducer;
